import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import routeMobileService from "../../services/routemobile.service";

// Get all Route Mobiles
export const fetchRouteMobiles = createAsyncThunk(
    "routeMobile/fetchRouteMobiles",
    async (_, thunkAPI) => {
        try {
            return await routeMobileService.getRouteMobiles();
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

// Get Route Mobile by ID
export const fetchRouteMobileById = createAsyncThunk(
    "routeMobile/fetchRouteMobileById",
    async (id, thunkAPI) => {
        try {
            return await routeMobileService.getRouteMobileById(id);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

// Add Route Mobile
export const createRouteMobile = createAsyncThunk(
    "routeMobile/createRouteMobile",
    async (data, thunkAPI) => {
        try {
            return await routeMobileService.addRouteMobile(data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

// Update Route Mobile
export const editRouteMobile = createAsyncThunk(
    "routeMobile/editRouteMobile",
    async ({ id, data }, thunkAPI) => {
        try {
            return await routeMobileService.updateRouteMobile(id, data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message
            );
        }
    }
);

const initialState = {
    routeMobiles: [],
    selectedRouteMobile: null,

    loading: false,
    submitting: false,
    error: null,

    success: false,
    message: "",
};

const routeMobileSlice = createSlice({
    name: "routeMobile",
    initialState,

    reducers: {
        clearRouteMobileError: (state) => {
            state.error = null;
        },

        clearRouteMobileSuccess: (state) => {
            state.success = false;
            state.message = "";
        },

        clearSelectedRouteMobile: (state) => {
            state.selectedRouteMobile = null;
        },
    },

    extraReducers: (builder) => {
        builder

            // Fetch all Route Mobiles
            .addCase(fetchRouteMobiles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRouteMobiles.fulfilled, (state, action) => {
                state.loading = false;
                state.routeMobiles =
                    action.payload?.data || action.payload?.routeMobiles || [];
            })
            .addCase(fetchRouteMobiles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Route Mobile by ID
            .addCase(fetchRouteMobileById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRouteMobileById.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedRouteMobile =
                    action.payload?.data || action.payload?.routeMobile || action.payload;
            })
            .addCase(fetchRouteMobileById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create Route Mobile
            .addCase(createRouteMobile.pending, (state) => {
                state.submitting = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createRouteMobile.fulfilled, (state, action) => {
                state.submitting = false;
                state.success = true;
                state.message = action.payload?.message || "Route Mobile created successfully";

                const newRouteMobile =
                    action.payload?.data || action.payload?.routeMobile;

                if (newRouteMobile) {
                    state.routeMobiles.unshift(newRouteMobile);
                }
            })
            .addCase(createRouteMobile.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })

            // Update Route Mobile
            .addCase(editRouteMobile.pending, (state) => {
                state.submitting = true;
                state.error = null;
                state.success = false;
            })
            .addCase(editRouteMobile.fulfilled, (state, action) => {
                state.submitting = false;
                state.success = true;
                state.message = action.payload?.message || "Route Mobile updated successfully";

                const updatedRouteMobile =
                    action.payload?.data || action.payload?.routeMobile;

                if (updatedRouteMobile) {
                    const index = state.routeMobiles.findIndex(
                        (item) => item._id === updatedRouteMobile._id
                    );

                    if (index !== -1) {
                        state.routeMobiles[index] = updatedRouteMobile;
                    }

                    state.selectedRouteMobile = updatedRouteMobile;
                }
            })
            .addCase(editRouteMobile.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearRouteMobileError,
    clearRouteMobileSuccess,
    clearSelectedRouteMobile,
} = routeMobileSlice.actions;

export default routeMobileSlice.reducer;